import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { scrollToHash } from "../utils/scroll";

const isHome = (pathname) =>
  pathname === "/" || pathname === "" || pathname.endsWith("/");

const dropdownBtnStyle = {
  background: "none",
  border: "none",
  borderRadius: "0",
  padding: "8px 2px",
  margin: "9px 20px 0",
  fontFamily: "Montserrat, sans-serif",
  fontSize: "15px",
  fontWeight: "400",
  color: "#000",
  textTransform: "uppercase",
  cursor: "pointer",
  lineHeight: "1.42857143",
  outline: "none",
  display: "inline-block",
  position: "relative",
};

export const Navigation = (props) => {
  const location = useLocation();
  const home = isHome(location.pathname);
  const [menuOpen, setMenuOpen] = useState(false);
  const [tracksOpen, setTracksOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const tracksRef = useRef(null);
  const aboutRef = useRef(null);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (tracksRef.current && !tracksRef.current.contains(e.target)) {
        setTracksOpen(false);
      }
      if (aboutRef.current && !aboutRef.current.contains(e.target)) {
        setAboutOpen(false);
      }
    };
    document.addEventListener("mousedown", handleOutsideClick);
    return () => document.removeEventListener("mousedown", handleOutsideClick);
  }, []);

  useEffect(() => {
    if (!home) return;
    const hash = (location.hash || "").replace(/^#/, "");
    if (hash) {
      const timeoutId = setTimeout(() => scrollToHash(hash), 200);
      return () => clearTimeout(timeoutId);
    }
  }, [home, location.pathname, location.hash]);

  const to = (section) => (home ? `#${section}` : `/#${section}`);

  const handleClick = (e, section) => {
    if (!home) return;
    e.preventDefault();
    setMenuOpen(false);
    window.history.pushState(null, "", `${location.pathname}#${section}`);
    if (section === "page-top") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      scrollToHash(section);
    }
  };

  const toggleMenu = () => setMenuOpen((open) => !open);

  return (
    <nav id="menu" className="navbar navbar-default navbar-fixed-top">
      <div className="container">
        <div className="navbar-header">
          <button
            type="button"
            className={`navbar-toggle collapsed ${menuOpen ? "" : "collapsed"}`}
            onClick={toggleMenu}
            aria-expanded={menuOpen}
            aria-controls="bs-example-navbar-collapse-1"
          >
            {" "}
            <span className="sr-only">Toggle navigation</span>{" "}
            <span className="icon-bar"></span>{" "}
            <span className="icon-bar"></span>{" "}
            <span className="icon-bar"></span>{" "}
          </button>
          <a
            className="navbar-brand page-scroll"
            href={to("page-top")}
            onClick={(e) => handleClick(e, "page-top")}
          >
            MedConnect
          </a>{" "}
        </div>

        <div
          className={`collapse navbar-collapse ${menuOpen ? "in" : ""}`}
          id="bs-example-navbar-collapse-1"
        >
          <ul className="nav navbar-nav navbar-right">
            <li>
              <Link to="/" onClick={() => setMenuOpen(false)}>
                Home
              </Link>
            </li>
            <li ref={tracksRef} className={`dropdown${tracksOpen ? " open" : ""}`}>
              <button
                style={dropdownBtnStyle}
                className="dropdown-toggle"
                onClick={() => setTracksOpen((o) => !o)}
              >
                Tracks <span className="caret"></span>
              </button>
              <ul className="dropdown-menu">
                <li>
                  <Link to="/premed" onClick={() => { setTracksOpen(false); setMenuOpen(false); }}>
                    Pre-Medical
                  </Link>
                </li>
                <li>
                  <Link to="/predent" onClick={() => { setTracksOpen(false); setMenuOpen(false); }}>
                    Pre-Dental
                  </Link>
                </li>
                <li>
                  <Link to="/prepa" onClick={() => { setTracksOpen(false); setMenuOpen(false); }}>
                    Pre-Physician Assistant
                  </Link>
                </li>
              </ul>
            </li>
            <li>
              <Link to="/get-involved" onClick={() => setMenuOpen(false)}>
                Get Involved
              </Link>
            </li>
            <li ref={aboutRef} className={`dropdown${aboutOpen ? " open" : ""}`}>
              <button
                style={dropdownBtnStyle}
                className="dropdown-toggle"
                onClick={() => setAboutOpen((o) => !o)}
              >
                About Us <span className="caret"></span>
              </button>
              <ul className="dropdown-menu">
                <li>
                  <Link to="/meet-the-team" onClick={() => { setAboutOpen(false); setMenuOpen(false); }}>
                    Meet the Team
                  </Link>
                </li>
                <li>
                  <Link to="/locations" onClick={() => { setAboutOpen(false); setMenuOpen(false); }}>
                    Locations
                  </Link>
                </li>
                <li>
                  <Link to="/events" onClick={() => { setAboutOpen(false); setMenuOpen(false); }}>
                    Events
                  </Link>
                </li>
              </ul>
            </li>
            <li>
              <a
                href="https://www.linkedin.com/company/medconnectinc/"
                target="_blank"
                rel="noopener noreferrer"
                className="nav-social"
                aria-label="LinkedIn"
                title="LinkedIn"
              >
                <i className="fa fa-linkedin" aria-hidden="true" />
              </a>
            </li>
            <li>
              <a
                href="https://www.instagram.com/medconnect.umass/"
                target="_blank"
                rel="noopener noreferrer"
                className="nav-social nav-social-icon"
                aria-label="Instagram"
                title="Instagram"
              >
                <svg className="instagram-icon" viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" />
                </svg>
              </a>
            </li>
          </ul>
        </div>
      </div>
    </nav>
  );
};